import { ref } from "vue";
import type { Ref, ComputedRef } from "vue";
import { useQueryClient } from "@tanstack/vue-query";
import { useTrpc } from "@app/vue/providers/TrpcProvider";
import { useShop } from "@app/vue/providers/ShopConnection";
import {
  invalidateAfterAcceptRequest,
  invalidateAfterRejectRequest,
} from "../data/invalidation";
import type { ReservationRequest } from "@lemmonhq/types";

export function useReservationRequestBatchActions(params: {
  allRequests: ComputedRef<ReservationRequest[]>;
  onAfterMutation?: () => void;
}) {
  const trpc = useTrpc();
  const queryClient = useQueryClient();
  const shop = useShop();
  const { onAfterMutation } = params;

  const selectedIds = ref<Set<string>>(new Set());
  const batchLoading = ref(false);

  const toggleSelect = (id: string) => {
    const next = new Set(selectedIds.value);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    selectedIds.value = next;
  };

  const selectAll = () => {
    selectedIds.value = new Set(params.allRequests.value.map((r) => r.id));
  };

  const clearSelection = () => {
    selectedIds.value = new Set();
  };

  const batchAccept = async () => {
    const ids = [...selectedIds.value];
    batchLoading.value = true;
    try {
      for (const id of ids) {
        try {
          await trpc.reservations.acceptRequest.mutate({
            request_id: id,
            table_ids: [],
          });
        } catch (e) {
          console.error(`Failed to accept request ${id}:`, e);
        }
      }
      clearSelection();
      await Promise.all(
        ids.map((id) =>
          invalidateAfterAcceptRequest(queryClient, { requestId: id }),
        ),
      );
      shop.refreshData();
      onAfterMutation?.();
    } finally {
      batchLoading.value = false;
    }
  };

  const batchReject = async () => {
    const ids = [...selectedIds.value];
    batchLoading.value = true;
    try {
      for (const id of ids) {
        try {
          await trpc.reservations.updateRequestStatus.mutate({
            request_id: id,
            status: "rejected",
          });
        } catch (e) {
          console.error(`Failed to reject request ${id}:`, e);
        }
      }
      clearSelection();
      await Promise.all(
        ids.map((id) =>
          invalidateAfterRejectRequest(queryClient, { requestId: id }),
        ),
      );
      shop.refreshData();
      onAfterMutation?.();
    } finally {
      batchLoading.value = false;
    }
  };

  return {
    selectedIds: selectedIds as Ref<Set<string>>,
    batchLoading: batchLoading as Ref<boolean>,
    toggleSelect,
    selectAll,
    clearSelection,
    batchAccept,
    batchReject,
  };
}
